import { useCallback } from 'react';
import { prop } from 'ramda';
import { useSetPlayerTags } from 'server';
import { useAppSelector } from 'js/store/hooks';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useFormatter } from 'js/hooks/useFormatter';
import { useGetErrorMessage } from 'js/hooks/server/hooks';

import { ADDRESS_DETAILS_KEYS } from '../../constants';
import { fieldToImsValue, getExtraImsFields } from '../../helpers';

export const useOnCombinedSubmit = ({
    fields,
    onSubmit,
    playerTags,
    submitTags,
    fetchPlayerTags,
    setValuesOnSubmit,
    hiddenByRulesFields,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest
}) => {
    const formatter = useFormatter();
    const isMounted = useIsMounted();
    const getErrorMessage = useGetErrorMessage();
    const setPlayerTags = useSetPlayerTags();
    const allowedCountries = useAppSelector(prop('allowedCountries'));

    return useCallback(async (formData, form) => {
        const data = fields
            .filter((field) => field.imsField && !hiddenByRulesFields.includes(field.name))
            .reduce((accumulator, field) => {
                const value = fieldToImsValue(formData, field, allowedCountries);
                const extraImsFields = getExtraImsFields(field.extraImsFields, value);

                return {
                    ...accumulator,
                    ...extraImsFields,
                    [field.imsField]: value
                };
            }, {});

        const addressDetails = ADDRESS_DETAILS_KEYS
            .reduce((accumulator, key) => ({
                ...accumulator,
                [key]: data[key]
            }), {});

        const dataMap = Object.keys(data)
            .filter((key) => !ADDRESS_DETAILS_KEYS.includes(key))
            .reduce((accumulator, key) => ({
                ...accumulator,
                [key]: data[key]
            }), {});

        const getTagExpirationDate = (expirationDate) => {
            if (!expirationDate || Number.isNaN(expirationDate)) {
                return;
            }

            const date = new Date();

            date.setDate(date.getDate() + Number(expirationDate));

            return formatter.date(date.toISOString(), '%Y-%m-%d');
        };

        const changedPlayerTags = fields
            .filter((field) => field.usePlayerTags && !hiddenByRulesFields.includes(field.name))
            .reduceRight((accumulator, field) => {
                const { name, type, expirationDate } = field.playerTags[0];
                const playerTag = playerTags.find(tag => tag.name === name) || {};
                const newValue = fieldToImsValue(formData, field);
                const prevValue = playerTag.value;
                const newExpirationDate = getTagExpirationDate(expirationDate);

                return newValue === prevValue || accumulator.find(tag => tag.name === name)
                    ? accumulator
                    : [
                    ...accumulator,
                    {
                        name,
                        type,
                        value: newValue,
                        ...newExpirationDate && { expirationDate: newExpirationDate }
                    }
                ];
            }, []);

        isMounted() && setValuesOnSubmit(formData);

        if (submitTags && changedPlayerTags.length) {
            try {
                await setPlayerTags({ tags: changedPlayerTags });
                await fetchPlayerTags();
            } catch (error) {
                return getErrorMessage(error);
            }
        }

        return await onSubmit({
            data,
            dataMap,
            fields,
            formData,
            tags: changedPlayerTags,
            addressDetails,
            requestReCaptchaToken,
            abortReCaptchaTokenRequest,
            form
        });
    }, [
        fields,
        onSubmit,
        formatter,
        isMounted,
        playerTags,
        submitTags,
        setPlayerTags,
        fetchPlayerTags,
        getErrorMessage,
        allowedCountries,
        setValuesOnSubmit,
        hiddenByRulesFields,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    ]);
};
